import * as ko from "knockout";
import {
    ICompany,
    ICompanySyncstep,
    IExportFlow,
    IExportTemplate,
    IMessageData,
    IObjectType,
    IRouteDefinitionParams,
    IRuleset
} from "../../index";
import PromiseRequest from "../../req/PromiseRequest";
import {MAX_DESCRIPTION_LENGTH, MAX_RESULTS_PER_PAGE, ObjectTypeId, SyncstepId} from "../../constants";
import {_} from 'lodash';
import {
    IExportFlowMutable,
    IExportTemplateMutable,
    TErrorManagers,
    TSynchronizeTemplateSections,
    TSynctemplateTabId,
    TTabs
} from "./index";
import {nsExportTemplate} from "./fn";
import {global} from "../../appContext";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {_d, createErrorMessage, getObjectDefinition, validateEmail} from "../../util/misc";
import {memoryStore} from "../../util/memorystore";
import {nsFa} from "../fa/fn";
import pubsub from "../../pubsub";
import {getCancelButton, getSubmitButton, setRouteHash} from "../../constants/RouteDefinitions";
import {TServiceName} from "../../constants/ApiServices";
import {nsRoute} from "../../util/route";
import {nsParentPath} from "./parentpath/fn";
import {ErrorObject} from "../../error/ErrorObject";
import {ButtonManager} from "../../util/ButtonManager";
import {Tab} from "../tab-list/Tab";
import {nsTabList} from "../tab-list/nsTabList";
import {REQUEST_MESSAGE} from "../../constants/Language";
import {nsSync} from "../../util/nsSync";
import {getSearchParamsFn} from "../../util/request";
import {ICreateRulesetEventParams, IUpdateRulesetEventParams} from "../ruleset/index";
import ERROR_AREA = nsExportTemplate.ERROR_AREA;
import Section = nsExportTemplate.Section;
import showDBPD = nsExportTemplate.showDBPD;
import i = nsFa.i;
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import TYPE = nsParentPath.TYPE;
import mutableHtml from './mutable.html?raw';

export let template: string = mutableHtml;

export abstract class Mutable extends ParsedRouteDataDecorator {

    Section = Section;

    protected availableObjectTypes!: IObjectType[];

    protected abstract icon: string;
    protected abstract label: string;
    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    isReady: KnockoutObservable<boolean> = ko.observable(true);
    readonly rulesets: IRuleset[] = [];

    badge = {
        parentpath: ko.pureComputed((): number => this.getRelevantExportFlows()
            .filter(nsExportTemplate.isExportFlowMutableParentPath)
            .length
        ),
        flowsexport: ko.pureComputed((): number => this.getRelevantExportFlows()
            .filter(nsExportTemplate.isExportFlowMutableOrdinaryExport)
            .length
        ),
        flowsimport: ko.pureComputed((): number => this.getRelevantExportFlows()
            .filter(nsExportTemplate.isExportFlowMutableOrdinaryImport)
            .length
        ),
        visma: ko.pureComputed((): string | null =>
            this.getRelevantExportFlows()
                .filter(nsExportTemplate.isExportFlowMutableVisma)
                .length
                ? i("fa-check")
                : null
        ),
    } as { [key in Exclude<TSynchronizeTemplateSections, 'general'>]: KnockoutComputed<string | number | null> };

    private toggleDaysBeforePermanentDelete: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly errName: ErrorObject;
    private readonly errObjectType: ErrorObject;
    private readonly errDescription: ErrorObject;
    private readonly errSyncstep: ErrorObject;
    private readonly errDaysBeforePermanentDelete: ErrorObject;

    private static getChecked(key: string): KnockoutComputed<string | null> {
        const obs = memoryStore.get(key);
        return ko.pureComputed((): string | null => obs() ? i("fa-check") : null);
    }

    protected submitRequestMessage!: string;
    protected submitSuccessMessage!: string;
    protected abstract reqSubmit: PromiseRequest;
    protected req!: PromiseRequest;
    protected company!: ICompany;

    // the current data which we'll let the user manipulate
    public data: IExportTemplateMutable = {
        $original: {
            active: true,
            daysbeforepermanentdelete: 0,
            deletefiles: false,
            description: '',
            editstriggerfullevaluation: false,
            exportflow: [],
            id: null,
            name: null,
            objecttype: null,
            ruleset: null,
            syncstep: null,
        },
        active: ko.observable(true),
        daysbeforepermanentdelete: ko.observable(0),
        deletefiles: ko.observable(false),
        description: ko.observable(''),
        editstriggerfullevaluation: ko.observable(false),
        exportflow: ko.observableArray<IExportFlowMutable>([]),
        name: ko.observable(""),
        objecttype: ko.observable(null),
        ruleset: ko.observable(null),
        syncstep: ko.observable(null),
    };

    protected kosub: KOSubscriptionTracker = new KOSubscriptionTracker();

    isFormValid = ko.computed((): boolean => this._isFormValid());
    isFormChanged = ko.pureComputed((): boolean => this._isFormChanged());

    private readonly MAX_DESCRIPTION_LENGTH = MAX_DESCRIPTION_LENGTH;

    private readonly tabs: TTabs;
    private readonly errorManagers: TErrorManagers;
    private readonly pageButtons: ButtonManager = new ButtonManager();

    constructor(params: IRouteDefinitionParams) {
        super(params);
        const cancelButton = getCancelButton();
        if (cancelButton) {
            this.pageButtons.add(cancelButton);
        }
        this.pageButtons.add(getSubmitButton({
            onClick: this.onSubmit.bind(this),
            enabled: () => this.isFormChanged() && this.isFormValid(),
        }))

        this.kosub.subscribeChanged(this.data.objecttype, objectType => {
            if (objectType && objectType.id != this.data.ruleset()?.objecttype.id) {
                this.data.ruleset(null)
            }
        });
        pubsub.on(this, 'ruleset.created', (_event: JQuery.Event, params: ICreateRulesetEventParams) => {
            if (!this.rulesets.some(ruleset => ruleset.id == params.newRuleset.id)) {
                this.rulesets.push(params.newRuleset);
            }
        });
        pubsub.on(this, 'ruleset.updated', (_event: JQuery.Event, params: IUpdateRulesetEventParams) => {
            const index = this.rulesets.findIndex(ruleset => ruleset.id == params.oldRuleset.id);
            if (index >= 0) {
                this.rulesets.splice(index, 1, params.newRuleset);
            }
        });
        this.tabs = {
            synctemplateinfo: this.__tabManager!.getTab('synctemplateinfo')!,
            synctemplateparentpath: this.__tabManager!.getTab('synctemplateparentpath')!,
            synctemplatevisma: this.__tabManager!.getTab('synctemplatevisma')!,
            synctemplateexportmappings: this.__tabManager!.getTab('synctemplateexportmappings')!,
            synctemplateimportmappings: this.__tabManager!.getTab('synctemplateimportmappings')!,
        }
        const setTabVisibility = (tab: Tab, fn: () => boolean) => {
            (tab.isVisible as KnockoutObservable<boolean | (() => boolean)>)(fn);
        };
        setTabVisibility(this.tabs.synctemplateparentpath, this.showSection.bind(this, Section.ParentPath));
        setTabVisibility(this.tabs.synctemplatevisma, this.showSection.bind(this, Section.Visma));
        setTabVisibility(this.tabs.synctemplateexportmappings, this.showSection.bind(this, Section.MappingsExport));
        setTabVisibility(this.tabs.synctemplateimportmappings, this.showSection.bind(this, Section.MappingsImport));

        this.errorManagers = {
            synctemplateinfo: this.tabs.synctemplateinfo.errorManager,
            synctemplateparentpath: this.tabs.synctemplateparentpath.errorManager,
            synctemplatevisma: this.tabs.synctemplatevisma.errorManager,
            synctemplateexportmappings: this.tabs.synctemplateexportmappings.errorManager,
            synctemplateimportmappings: this.tabs.synctemplateimportmappings.errorManager,
        }

        this.errName = new ErrorObject()
        this.errName.addValidator({
            fn: () => !!this.data.name(),
            message: '((invalid)) ((name|ac))',
        })

        this.errObjectType = new ErrorObject()
        this.errObjectType.addValidator({
            fn: () => this.hasObjectType(),
            message: '((invalid)) ((object type|ac))',
        })

        this.errDescription = new ErrorObject()
        this.errDescription.addValidator({
            fn: () => this.data.description().length <= this.MAX_DESCRIPTION_LENGTH,
            message: '((invalid)) ((length|ac))',
        })

        this.errSyncstep = new ErrorObject()
        this.errSyncstep.addValidator({
            fn: () => !!this.data.syncstep(),
            message: '((invalid)) ((synchronization step|ac))',
            prerequisite: () => !!this.data.objecttype(),
        })

        this.errDaysBeforePermanentDelete = new ErrorObject()
        this.errDaysBeforePermanentDelete.addValidator({
            fn: () => this._validateDBPD(),
            message: 'invalid value',
            prerequisite: () => !!this.toggleDaysBeforePermanentDelete(),
        })

        this.errorManagers.synctemplateinfo.addErrorObject(this.errName)
        this.errorManagers.synctemplateinfo.addErrorObject(this.errObjectType)
        this.errorManagers.synctemplateinfo.addErrorObject(this.errDescription)
        this.errorManagers.synctemplateinfo.addErrorObject(this.errSyncstep)
        this.errorManagers.synctemplateinfo.addErrorObject(this.errDaysBeforePermanentDelete)
    }

    protected initRequest(): Promise<any> {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});
        const companyAndObjectTypesRequest = global.getPromiseRequest("getcompany")
            .request()
            .then((data: ICompany[]) => {
                if (data[0]) {
                    this.company = data[0];
                }
            })
            .then(() => global.getPromiseRequest('getobjecttypes')
                .request()
                .then((data: IObjectType[]) => {
                    const availableObjectTypes = [
                        ObjectTypeId.User,
                        ObjectTypeId.Department,
                        ObjectTypeId.Group,
                    ];
                    if (this.data.$original.id || global.permissionValidator.isMinOrganizationAdmin()) {
                        availableObjectTypes.push(ObjectTypeId.System)
                    }
                    if (this.company.eformforeadm) {
                        availableObjectTypes.push(ObjectTypeId.FormData);
                    }
                    this.availableObjectTypes = availableObjectTypes
                        .map(id => data.find(o => o.id == id))
                        .filter((o): o is IObjectType => !!o);
                })
            );

        const rulesetsRequest = global.getPromiseRequest('searchruleset')
            .request(getSearchParamsFn('searchruleset')({resultsprpage: MAX_RESULTS_PER_PAGE}))
            .then((data: IRuleset[]) => {
                this.rulesets.push(...data);
            });

        return Promise.all([companyAndObjectTypesRequest, rulesetsRequest]);
    }

    protected onSuccess() {
        this.toggleDaysBeforePermanentDelete(!!this.data.$original.daysbeforepermanentdelete);
        this.messageData(null);
        this.isReady(true);
    }

    protected _isFormValid(): boolean {
        return this.__tabManager?.validate() ?? false;
    }

    protected _isFormChanged(): boolean {
        const isEqual = _.isEqual(this.data.$original, this.submitData());
        return !isEqual;
    }

    onSubmit() {
        this.isReady(false);
        this.messageData({message: this.submitRequestMessage});

        let data = this.submitData();
        data = this.trimData(data)
        data.exportflow = [];
        const syncstep = data.syncstep;
        if (!syncstep) {
            return;
        }
        data.syncstep = {id: syncstep.id} as ICompanySyncstep

        const deleted = this.getDeleted();
        const added = this.getAdded();
        const updated = this.getUpdated();
        const alreadyActive = !!this.data.$original.id && !!this.data.$original.active;
        const activateAfterFlows = data.active
            && !alreadyActive
            && !!(deleted.length || added.length || updated.length)
            && this.data.objecttype()?.id == ObjectTypeId.System
            && this.data.syncstep()?.syncstep?.id == SyncstepId.Synchronization;
        const json = JSON.stringify(activateAfterFlows ? {...data, active: false} : data);

        this.reqSubmit.abort();

        Promise
            // first create/update the template
            .resolve(this.reqSubmit.request({jexporttemplate: json}))
            // keep the changes
            .then((data: IExportTemplate[]) => {
                const saved = data[0];
                if (!saved) {
                    throw new Error('Missing export template after submit');
                }
                global.snackbarData({message: this.submitSuccessMessage});
                this.data.$original = saved;
            })
            // then delete
            .then(() => Promise
                .all(deleted
                    .map((item: IExportFlow) => this.request("deleteexportflow", {id: item.id}))))
            // then add
            .then(() => Promise
                .all(added
                    .map((item: IExportFlow) => {
                        const _item = this.trimFlow(_.clone(item));
                        _item.id = 0;
                        _item.expression = encodeURIComponent(_item.expression);
                        _item.targetattributetype = _item.targetattributetype ?? 'String'
                        const jexportflow = JSON.stringify(_item);
                        return this.request("createexportflow", {
                            exporttemplateid: this.data.$original.id,
                            jexportflow: jexportflow,
                        });
                    })
                ))
            // then update
            .then(() => Promise
                .all(updated
                    .map((item: IExportFlow) => {
                        const _item = this.trimFlow(_.clone(item));
                        _item.expression = encodeURIComponent(_item.expression);
                        _item.targetattributetype = _item.targetattributetype ?? 'String'
                        const jexportflow = JSON.stringify(_item);
                        return this.request("updateexportflow", {jexportflow: jexportflow})
                    })
                ))
            // then activate if needed
            .then(() => {
                if (!activateAfterFlows) {
                    return;
                }
                const activated = JSON.stringify({...data, id: this.data.$original.id, active: true});
                return this.request("updateexporttemplate", {jexporttemplate: activated})
                    .then((res: IExportTemplate[]) => {
                        if (res[0]) {
                            this.data.$original = res[0];
                        }
                    });
            })
            // remove cache and show details page
            .then(() => {
                this.killCache();
                setRouteHash('synctemplatedetails', {id: this.data.$original.id});
            })
            .catch((reason: string) => {
                this.killCache();
                this.messageData(createErrorMessage(String(reason)));
                this.isReady(true);
            });
    }

    private killCache() {
        global.cacheManager.removeCache('getruleset');
        global.cacheManager.removeCache('searchruleset');
        global.cacheManager.removeCache("searchexporttemplate");
        global.cacheManager.removeCache("getexporttemplate");
        global.cacheManager.removeCache('getehubprocess');
    }

    private request(serviceName: TServiceName, params: any): Promise<any> {
        return new PromiseRequest(serviceName).request(params);
    }

    submitData = ko.pureComputed((): IExportTemplate => {
        return {
            active: this.data.active(),
            daysbeforepermanentdelete: this.showDBPD() && !!this.toggleDaysBeforePermanentDelete() ? this.data.daysbeforepermanentdelete() : 0,
            deletefiles: this.showDBPD() && this.data.deletefiles(),
            description: this.data.description(),
            editstriggerfullevaluation: this.data.editstriggerfullevaluation(),
            exportflow: this.getRelevantExportFlows().map((item: IExportFlowMutable) => this.mutableToStatic(item)),
            id: this.data.$original.id ?? 0,
            name: this.data.name(),
            objecttype: this.data.objecttype()!,
            ruleset: this.data.ruleset()!,
            syncstep: this.data.syncstep()!,
        };
    });

    private trimData(data: IExportTemplate): IExportTemplate {
        return _.defaultsDeep({
            description: data.description.trim(),
            name: data.name.trim(),
        }, data);
    }

    private trimFlow(data: IExportFlow): IExportFlow {
        return _.defaultsDeep({expression: data.expression?.trim?.() ?? data.expression}, data)
    }

    private mutableToStatic(efm: IExportFlowMutable): IExportFlow {
        const _default: IExportFlow = {
            expression: efm.expression(),
            id: efm.$original.id,
            inbound: efm.$original.inbound,
            nullable: efm.nullable(),
            objecttype: efm.objecttype()!,
            ruleset: efm.ruleset()!,
            sourceattribute: "",
            syncnext: efm.syncnext(),
            syncstep: this.data.syncstep()!,
            targetattribute: "",
            targetattributetype: "String",
            updateforcesdeltasync: false,
        };
        if (efm.$original.inbound) {
            return _.defaults({
                objecttype: efm.objecttype()!,
                sourceattribute: efm.sourceattribute()?.name ?? "",
                targetattribute: efm.targetattribute()?.name ?? "",
                targetattributetype: efm.sourceattribute()?.type ?? "String",
                updateforcesdeltasync: efm.updateforcesdeltasync()
            }, _default);
        } else {
            const targetAttr = efm.targetattribute();
            const referenceObjectType = targetAttr?.referenceobjecttype;
            return _.defaults({
                objecttype: referenceObjectType ?? efm.objecttype()!,
                sourceattribute: referenceObjectType ? String(efm.sourceattribute() ?? "") : "",
                targetattribute: targetAttr?.name ?? "",
                targetattributetype: targetAttr?.type ?? "String",
            }, _default);
        }
    }

    private getDeleted(): IExportFlow[] {
        const original = this.data.$original.exportflow;
        const current = this.getRelevantExportFlows();
        return original
            .filter((o: IExportFlow) => !current.find((c: IExportFlowMutable) => o.id == c.$original.id));
    }

    protected getAdded(): IExportFlow[] {
        const current = this.getRelevantExportFlows().map((efm: IExportFlowMutable) => this.mutableToStatic(efm));
        return current.filter((c: IExportFlow) => c.id == 0);
    }

    protected getUpdated(): IExportFlow[] {
        const current = this.getRelevantExportFlows().map((efm: IExportFlowMutable) => this.mutableToStatic(efm));
        const original = this.data.$original.exportflow;
        return current
            .filter((c: IExportFlow) => original.find((o: IExportFlow) => (c.id == o.id) && !_.isEqual(c, o)));
    }

    private hasObjectType(): boolean {
        return !!this.data.objecttype();
    }

    private isObjectType(objectTypeId: ObjectTypeId): boolean {
        return this.hasObjectType() && this.data.objecttype()!.id == objectTypeId;
    }

    private getRelevantExportFlows(): IExportFlowMutable[] {
        return this.data.exportflow().filter(this.isExportFlowRelevant.bind(this));
    }

    private isExportFlowRelevant(item: IExportFlowMutable): boolean {
        return true;
    }

    private showSection(section: Section): boolean {
        switch (section) {
            case Section.MappingsExport: {
                const a = this.hasObjectType()
                const b = !!this.data.syncstep()
                const c = this.data.syncstep()?.syncstep.id != SyncstepId.Synchronization
                return a && b && c
            }
            case Section.MappingsImport: {
                const a = this.hasObjectType()
                const b = !!this.data.syncstep()
                return a && b
            }
            case Section.ParentPath:
                return this.hasObjectType() && this.isSyncstepOnPremAD()
            case Section.Ruleset:
                return this.hasObjectType();
            case Section.Syncstep:
                return this.hasObjectType() && !this.data.exportflow().length;
            case Section.Visma: {
                const a = this.company?.importformathr.name == 'Visma';
                const b = this.isObjectType(ObjectTypeId.User);
                const c = this.isSyncstepOnPremAD();
                return a && b && c;
            }
            default:
                _d.warn("No such section:", section);
                return false;
        }
    }

    protected showEdit(section: Section): boolean {
        switch (section) {
            case Section.ObjectType:
                return !this.data.exportflow().length;
            default:
                _d.warn("No such section:", section);
                return false;
        }
    }

    private _validateDBPD(): boolean {
        if (!this.showDBPD() || !this.toggleDaysBeforePermanentDelete()) {
            return true;
        }
        const val = this.data.daysbeforepermanentdelete();
        return !isNaN(val) && val >= 1 && val <= 1000;
    }

    showDBPD(): boolean {
        return showDBPD(this.data.objecttype(), this.data.syncstep());
    }

    private getObjectDefinition = getObjectDefinition;

    private fnFilterCompanySyncstep(item: ICompanySyncstep): boolean {
        const isFormData = this.isObjectType(ObjectTypeId.FormData);
        const a = [SyncstepId.Export_Form_to_eAdm, SyncstepId.Export_eHub_to_eAdm_Permissions].includes(item.syncstep.id);
        const b = item.syncstep.isexportable && !a;
        return isFormData ? a : b;
    }

    private isSyncstepOnPremAD = ko.pureComputed((): boolean => {
        return nsSync.isSyncstepOnPremAD(this.data.syncstep()?.syncstep.id)
    })

    public trackFlowErrors(flow: IExportFlowMutable, tabId?: TSynctemplateTabId) {
        if (!tabId) {
            tabId = this.getTabIdFromFlow(flow);
        }

        const errorManager = this.errorManagers[tabId];
        errorManager.addErrorObject(flow.errExpression)
        errorManager.addErrorObject(flow.errRuleset)
        errorManager.addErrorObject(flow.errSourceAttribute)
        errorManager.addErrorObject(flow.errTargetAttribute)
        errorManager.addErrorObject(flow.errType)

        flow.errTargetAttribute.addValidator({
            fn: () => !!flow.targetattribute(),
            message: 'invalid s|s:((target|ac))',
        })

        switch (tabId) {
            case "synctemplateparentpath":
                this.initErrObjParentPath(flow);
                break;
            case "synctemplatevisma":
                this.initErrObjVisma(flow);
                break;
            case "synctemplateexportmappings":
                this.initErrObjExport(flow);
                break;
            case "synctemplateimportmappings":
                this.initErrObjImport(flow);
                break;
        }
    }

    private getTabIdFromFlow(flow: IExportFlowMutable): TSynctemplateTabId {
        if (nsExportTemplate.isExportFlowMutableParentPath(flow)) {
            return 'synctemplateparentpath'
        } else if (nsExportTemplate.isExportFlowMutableVisma(flow)) {
            return 'synctemplatevisma'
        } else if (nsExportTemplate.isExportFlowMutableOrdinaryExport(flow)) {
            return 'synctemplateexportmappings'
        } else if (nsExportTemplate.isExportFlowMutableOrdinaryImport(flow)) {
            return 'synctemplateimportmappings'
        } else {
            _d.error('Tab id not resolved for', flow)
            throw Error('Tab id not resolved')
        }
    }

    private initErrObjParentPath(flow: IExportFlowMutable) {
        flow.errType.addValidator({
            fn: () => !!flow.type(),
            message: 'invalid type',
        })
        flow.errExpression.addValidator({
            fn: () => !!flow.expression(),
            message: 'invalid value',
            prerequisite: () => !!flow.type(),
        })

        flow.errSourceAttribute.addValidator({
            fn: () => !!flow.sourceattribute(),
            message: 'invalid s|s:((remote key|ac))',
            prerequisite: () => !!flow.type() && flow.type() == TYPE.DepartmentReferenceAttribute && this.data.syncstep()?.syncstep?.id != SyncstepId.Synchronization,
        })

        flow.errRuleset.addValidator({
            fn: () => {
                const flows = this.data.exportflow().filter(flow => nsExportTemplate.isExportFlowMutableParentPath(flow))
                const rulesetId = flow.ruleset()?.id;
                return !flows.find(f => flow !== f && rulesetId === f.ruleset()?.id)
            },
            message: 'must be unique',
        })
    }

    private initErrObjVisma(flow: IExportFlowMutable) {
    }

    private initErrObjExport(flow: IExportFlowMutable) {
        flow.errRuleset.addValidator({
            fn: () => {
                const duplicateFlow = this
                    .getRelevantExportFlows()
                    .filter(nsExportTemplate.isExportFlowMutableOrdinaryExport)
                    .find(f => flow != f && flow.targetattribute()?.name == f.targetattribute()?.name && flow.ruleset()?.id == f.ruleset()?.id)
                return !duplicateFlow;
            },
            message: 'must be unique',
            prerequisite: () => !!flow.targetattribute(),
        })
        flow.errExpression.addValidator({
            fn: () => {
                const expression = flow.expression();
                switch (flow.targetattribute()?.name) {
                    case "mailPath": {
                        const regExp = /^(http|ftp|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])?/i;
                        return regExp.test(expression);
                    }
                    default:
                        return true
                }
            },
            message: 'invalid s|s:((url))',
            prerequisite: () => !!flow.targetattribute(),
        })
        flow.errExpression.addValidator({
            fn: () => {
                const expression = flow.expression();
                switch (flow.targetattribute()?.name) {
                    case "TargetAddress": {
                        const isDynamicExpression = expression.indexOf('[') != -1 && expression.indexOf(']') != -1;
                        return isDynamicExpression ? !!expression : validateEmail(expression);
                    }
                    default:
                        return true
                }
            },
            message: 'invalid s|s:((email|ac))',
            prerequisite: () => !!flow.targetattribute(),
        })
    }

    private initErrObjImport(flow: IExportFlowMutable) {
        flow.errSourceAttribute.addValidator({
            fn: () => !!flow.sourceattribute(),
            message: 'invalid s|s:((source|ac))',
            prerequisite: () => this.data.syncstep()?.syncstep?.id != SyncstepId.Synchronization,
        })
    }

    public untrackFlowErrors(flow: IExportFlowMutable) {
        const errorManager = this.errorManagers[this.getTabIdFromFlow(flow)];
        errorManager.removeErrorObject(flow.errExpression)
        errorManager.removeErrorObject(flow.errRuleset)
        errorManager.removeErrorObject(flow.errSourceAttribute)
        errorManager.removeErrorObject(flow.errTargetAttribute)
        errorManager.removeErrorObject(flow.errType)
    }

    getRulesetObjectType(): ObjectTypeId {
        const objectType = this.data.objecttype();
        if (!objectType) {
            return ObjectTypeId.User;
        }
        if (objectType.id == ObjectTypeId.System) {
            return ObjectTypeId.User
        }
        return objectType.id
    }

    override dispose() {
        super.dispose();
        this.reqSubmit.abort();
        this.kosub.unsuscribeAll();
        this.isFormValid.dispose();
        pubsub.off(this);
        global.errorAreas.dispose(ERROR_AREA);
    }
}
